/**
 * AI Configuration Service
 *
 * T009: getAiSettings, saveAiSettings
 * Manages user AI configuration (provider, privacy controls, feature toggles).
 * API keys are stored server-side via Edge Functions; client never receives raw keys.
 *
 * @see specs/009-enable-ai-configuration/contracts/ai-configuration-service.md
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import type {
  AiSettings,
  AiSettingsResponse,
  SaveAiSettingsInput,
  SuccessResponse,
  UsageAnalyticsResponse,
  SpendLimitCheckResponse,
} from '../types/ai-configuration';
import { validateSaveAiSettingsInput } from '../utils/ai-configuration-validation';
import { buildInsightTimeRangeSavePayload } from '../utils/insight-time-range';
import type { AiProvider, QualityLevel } from '../types/ai-configuration';

/** Database row shape for user_ai_settings (not yet in database.types.ts) */
interface UserAiSettingsRow {
  id: string;
  user_id: string;
  provider: string;
  api_key_encrypted: string | null;
  monthly_spend_limit_cents: number | null;
  quality_level: string;
  cache_ai_responses: boolean;
  batch_processing: boolean;
  data_sharing_opt_out: boolean;
  sensitive_data_masking: boolean;
  data_retention_days: number | null;
  review_categorisations_before_apply: boolean;
  require_budget_approval: boolean;
  smart_categorisation_enabled: boolean;
  smart_categorisation_confidence_threshold: number;
  smart_insights_enabled: boolean;
  budget_analysis_enabled: boolean;
  unusual_expense_detection_enabled: boolean;
  chat_history_retention_mode?: string | null;
  chat_history_retention_days?: number | null;
  insight_refresh_frequency?: string | null;
  insight_time_range_days?: number | null;
  insight_custom_start_date?: string | null;
  insight_custom_end_date?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** T011: In-memory set of users who have used one-time override this session */
const overrideUsedThisSession = new Set<string>();

/** Turn raw Edge Function errors into user-friendly, actionable messages */
function toEdgeFunctionUserMessage(raw: string): string {
  const lower = raw.toLowerCase();
  if (
    lower.includes('failed to send') ||
    lower.includes('cors') ||
    lower.includes('fetch') ||
    lower.includes('network') ||
    lower.includes('blocked')
  ) {
    return 'The AI service could not be reached. This usually means the Edge Functions are not deployed. Deploy them with: supabase functions deploy ai-test-connection ai-save-config ai-get-usage --verify-jwt';
  }
  if (lower.includes('404') || lower.includes('not found')) {
    return 'The AI Edge Function is not deployed. Run: supabase functions deploy ai-test-connection ai-save-config ai-get-usage --verify-jwt';
  }
  if (lower.includes('401') || lower.includes('unauthorized') || lower.includes('jwt')) {
    return 'Session expired. Please sign out and sign in again, then try again.';
  }
  return raw;
}

type EdgeFunctionResultPayload = {
  success?: boolean;
  error?: string;
  message?: string;
};

/** Parse JSON body from supabase.functions.invoke when status is non-2xx. */
async function readEdgeFunctionPayload(
  data: unknown,
  error: unknown
): Promise<EdgeFunctionResultPayload | null> {
  if (data && typeof data === 'object') {
    return data as EdgeFunctionResultPayload;
  }

  if (!error || typeof error !== 'object') {
    return null;
  }

  try {
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      const bodyJson = await ctx.json();
      if (bodyJson && typeof bodyJson === 'object') {
        return bodyJson as EdgeFunctionResultPayload;
      }
    }
  } catch {
    // ignore body parse errors
  }

  return null;
}

/** Extract a readable message from provider JSON error bodies. */
function normalizeProviderErrorMessage(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) {
    return trimmed;
  }

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const nestedError = parsed.error;

    if (typeof nestedError === 'string' && nestedError.trim()) {
      return nestedError.trim();
    }

    if (nestedError && typeof nestedError === 'object') {
      const nested = nestedError as Record<string, unknown>;
      if (typeof nested.message === 'string' && nested.message.trim()) {
        return nested.message.trim();
      }
    }

    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    // keep original string
  }

  return trimmed;
}

function edgeFunctionErrorMessage(
  payload: EdgeFunctionResultPayload | null,
  fallback: string
): string {
  if (typeof payload?.error === 'string' && payload.error.trim()) {
    return normalizeProviderErrorMessage(payload.error);
  }
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return normalizeProviderErrorMessage(payload.message);
  }
  return normalizeProviderErrorMessage(fallback);
}

const DEFAULT_AI_SETTINGS: AiSettings = {
  provider: 'openai',
  apiKeyMasked: null,
  monthlySpendLimitCents: null,
  qualityLevel: 'balanced',
  cacheAiResponses: true,
  batchProcessing: true,
  dataSharingOptOut: false,
  sensitiveDataMasking: true,
  dataRetentionDays: null,
  reviewCategorisationsBeforeApply: false,
  requireBudgetApproval: false,
  smartCategorisationEnabled: false,
  smartCategorisationConfidenceThreshold: 85,
  smartInsightsEnabled: false,
  budgetAnalysisEnabled: false,
  unusualExpenseDetectionEnabled: false,
  insightRefreshFrequency: 'daily',
  insightTimeRangeDays: 90,
};

/** Mask API key for display when key is configured (never expose raw key) */
function maskApiKey(hasKey: boolean): string | null {
  return hasKey ? 'sk-••••••••' : null;
}

function rowToAiSettings(row: UserAiSettingsRow): AiSettings {
  return {
    provider: row.provider as AiProvider,
    apiKeyMasked: maskApiKey(!!row.api_key_encrypted),
    monthlySpendLimitCents: row.monthly_spend_limit_cents,
    qualityLevel: row.quality_level as QualityLevel,
    cacheAiResponses: row.cache_ai_responses ?? true,
    batchProcessing: row.batch_processing ?? true,
    dataSharingOptOut: row.data_sharing_opt_out ?? false,
    sensitiveDataMasking: row.sensitive_data_masking ?? true,
    dataRetentionDays: row.data_retention_days,
    reviewCategorisationsBeforeApply: row.review_categorisations_before_apply ?? false,
    requireBudgetApproval: row.require_budget_approval ?? false,
    smartCategorisationEnabled: row.smart_categorisation_enabled ?? false,
    smartCategorisationConfidenceThreshold:
      row.smart_categorisation_confidence_threshold ?? 85,
    smartInsightsEnabled: row.smart_insights_enabled ?? false,
    budgetAnalysisEnabled: row.budget_analysis_enabled ?? false,
    unusualExpenseDetectionEnabled: row.unusual_expense_detection_enabled ?? false,
    insightRefreshFrequency: (row.insight_refresh_frequency as any) ?? 'daily',
    insightTimeRangeDays: row.insight_time_range_days ?? 90,
    insightCustomStartDate: row.insight_custom_start_date ?? null,
    insightCustomEndDate: row.insight_custom_end_date ?? null,
  };
}

function inputToDbRow(
  input: SaveAiSettingsInput & { provider?: AiProvider; qualityLevel?: QualityLevel },
  userId: string
): Record<string, unknown> {
  const row: Record<string, unknown> = {
    user_id: userId,
    updated_at: new Date().toISOString(),
  };
  if (input.provider !== undefined) row.provider = input.provider;
  if (input.monthlySpendLimitCents !== undefined)
    row.monthly_spend_limit_cents = input.monthlySpendLimitCents;
  if (input.qualityLevel !== undefined) row.quality_level = input.qualityLevel;
  if (input.cacheAiResponses !== undefined)
    row.cache_ai_responses = input.cacheAiResponses;
  if (input.batchProcessing !== undefined)
    row.batch_processing = input.batchProcessing;
  if (input.dataSharingOptOut !== undefined)
    row.data_sharing_opt_out = input.dataSharingOptOut;
  if (input.sensitiveDataMasking !== undefined)
    row.sensitive_data_masking = input.sensitiveDataMasking;
  if (input.dataRetentionDays !== undefined)
    row.data_retention_days = input.dataRetentionDays;
  if (input.reviewCategorisationsBeforeApply !== undefined)
    row.review_categorisations_before_apply =
      input.reviewCategorisationsBeforeApply;
  if (input.requireBudgetApproval !== undefined)
    row.require_budget_approval = input.requireBudgetApproval;
  if (input.smartCategorisationEnabled !== undefined)
    row.smart_categorisation_enabled = input.smartCategorisationEnabled;
  if (input.smartCategorisationConfidenceThreshold !== undefined)
    row.smart_categorisation_confidence_threshold =
      input.smartCategorisationConfidenceThreshold;
  // smart_insights_enabled and budget_analysis_enabled were used by legacy AI features.
  // We intentionally ignore any incoming values from the UI so that new configuration
  // is driven by the Insights feature instead.
  if (input.unusualExpenseDetectionEnabled !== undefined)
    row.unusual_expense_detection_enabled = input.unusualExpenseDetectionEnabled;
  if (input.insightRefreshFrequency !== undefined)
    row.insight_refresh_frequency = input.insightRefreshFrequency;
  if (input.insightTimeRangeDays !== undefined)
    row.insight_time_range_days = input.insightTimeRangeDays;
  if (input.insightCustomStartDate !== undefined)
    row.insight_custom_start_date = input.insightCustomStartDate;
  if (input.insightCustomEndDate !== undefined)
    row.insight_custom_end_date = input.insightCustomEndDate;
  return row;
}

export const aiConfigurationService = {
  /**
   * Get user's AI settings. Returns masked API key (e.g. sk-••••••••) never raw.
   */
  async getAiSettings(userId?: string): Promise<AiSettingsResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      const { data: row, error } = await (supabase as any)
        .from('user_ai_settings')
        .select('*')
        .eq('user_id', currentUserId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return { data: { ...DEFAULT_AI_SETTINGS } };
        }
        return {
          error: {
            code: 'NOT_FOUND',
            message: error.message || 'Settings not found',
          },
        };
      }

      return { data: rowToAiSettings(row as UserAiSettingsRow) };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to get AI settings';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /**
   * Save AI settings. API key (if provided) is sent to Edge Function for secure storage.
   */
  async saveAiSettings(
    userId: string | undefined,
    input: SaveAiSettingsInput
  ): Promise<SuccessResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      const validation = validateSaveAiSettingsInput(input);
      if (!validation.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: validation.errors.join('; '),
          },
        };
      }
      const validated = validation.data;

      const provider = validated.provider ?? 'openai';
      const qualityLevel = validated.qualityLevel ?? 'balanced';
      const hasApiKey = !!(validated.apiKey && validated.apiKey.trim().length > 0);
      const clearApiKey = validated.clearApiKey === true;

      if (hasApiKey && provider !== 'none') {
        const testResult = await this.testConnection(
          currentUserId,
          validated.apiKey!,
          provider
        );
        if (testResult.error) {
          if (testResult.error.code === 'INVALID_API_KEY') {
            return { error: testResult.error };
          }
          return {
            error: {
              code: testResult.error.code,
              message: testResult.error.message,
            },
          };
        }
      }

      if (hasApiKey || clearApiKey) {
        const edgePayload: Record<string, unknown> = {
          userId: currentUserId,
          ...inputToDbRow(
            { ...validated, provider, qualityLevel },
            currentUserId
          ),
        };
        if (hasApiKey) edgePayload.apiKey = validated.apiKey;
        if (clearApiKey) edgePayload.clearApiKey = true;

        const { data: fnData, error: fnError } = await (supabase as any).functions.invoke(
          'ai-save-config',
          { body: edgePayload }
        );

        const fnPayload = await readEdgeFunctionPayload(fnData, fnError);

        if (fnError || fnPayload?.success === false) {
          const raw = edgeFunctionErrorMessage(
            fnPayload,
            fnError?.message || 'Failed to save configuration'
          );
          if (
            raw.toLowerCase().includes('api key') ||
            raw.toLowerCase().includes('invalid')
          ) {
            return { error: { code: 'INVALID_API_KEY', message: raw } };
          }
          return {
            error: {
              code: 'UNKNOWN_ERROR',
              message: toEdgeFunctionUserMessage(raw),
            },
          };
        }

        return { data: { success: true } };
      }

      const dbPayload = inputToDbRow(
        { ...validated, provider, qualityLevel },
        currentUserId
      );
      delete dbPayload.updated_at;
      const upsertRow = {
        ...dbPayload,
        user_id: currentUserId,
        provider,
        quality_level: qualityLevel,
        updated_at: new Date().toISOString(),
      };

      const { error: dbError } = await (supabase as any)
        .from('user_ai_settings')
        .upsert(upsertRow, { onConflict: 'user_id' });

      if (dbError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: dbError.message || 'Failed to save settings',
          },
        };
      }

      return { data: { success: true } };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save AI settings';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /**
   * Test API key and provider configuration. Calls Edge Function.
   * When useStoredKey is true and apiKey is empty, uses key from vault (client never has raw key).
   * Retries once on 429, timeout, or 5xx.
   */
  async testConnection(
    userId: string | undefined,
    apiKey: string,
    provider: string,
    useStoredKey?: boolean
  ): Promise<SuccessResponse> {
    const body: Record<string, unknown> = { provider };
    if (useStoredKey && !apiKey.trim()) {
      body.useStoredKey = true;
    } else {
      body.apiKey = apiKey.trim();
    }
    const invoke = async () =>
      (supabase as any).functions.invoke('ai-test-connection', { body });

    const isRetryable = (err: { message?: string } | null): boolean => {
      const msg = (err?.message || '').toLowerCase();
      return (
        msg.includes('429') ||
        msg.includes('timeout') ||
        msg.includes('500') ||
        msg.includes('502') ||
        msg.includes('503')
      );
    };

    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      let result = await invoke();
      if (result.error && isRetryable(result.error)) {
        result = await invoke();
      }

      const payload = await readEdgeFunctionPayload(result.data, result.error);

      if (payload?.success === true) {
        return { data: { success: true } };
      }

      const raw = edgeFunctionErrorMessage(
        payload,
        result.error?.message || 'Connection test failed'
      );

      if (
        raw.toLowerCase().includes('invalid') ||
        raw.toLowerCase().includes('key') ||
        raw.toLowerCase().includes('401') ||
        raw.toLowerCase().includes('403') ||
        raw.toLowerCase().includes('unauthorized') ||
        raw.toLowerCase().includes('authentication')
      ) {
        return { error: { code: 'INVALID_API_KEY', message: raw } };
      }

      if (result.error || payload?.success === false) {
        return {
          error: {
            code: 'PROVIDER_ERROR',
            message: toEdgeFunctionUserMessage(raw),
          },
        };
      }

      return {
        error: {
          code: 'INVALID_API_KEY',
          message: raw,
        },
      };
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Connection test failed';
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: toEdgeFunctionUserMessage(raw),
        },
      };
    }
  },

  /**
   * T010/T022: Get usage analytics for the Usage Analytics tab.
   * Calls ai-get-usage Edge Function; falls back to direct ai_usage_events read on failure.
   * Time saved = 1 min per categorisation accepted.
   */
  async getUsageAnalytics(
    userId?: string,
    month?: string
  ): Promise<UsageAnalyticsResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      const monthStr = month ?? new Date().toISOString().slice(0, 7);

      const { data: fnData, error: fnError } = await (supabase as any).functions.invoke(
        'ai-get-usage',
        { body: { month: monthStr } }
      );

      if (!fnError && fnData && typeof fnData === 'object' && 'thisMonthCostCents' in fnData) {
        return {
          data: {
            thisMonthCostCents: fnData.thisMonthCostCents ?? 0,
            apiRequests: fnData.apiRequests ?? 0,
            acceptedRatePercent: fnData.acceptedRatePercent ?? 0,
            correctRatePercent: fnData.correctRatePercent ?? 0,
            timeSavedHours: fnData.timeSavedHours ?? 0,
            recentActivity: fnData.recentActivity ?? [],
            performanceSummary: fnData.performanceSummary ?? 'No AI activity yet',
          },
        };
      }

      return this.getUsageAnalyticsFromDb(currentUserId, monthStr);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to get usage analytics';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /** Fallback: read usage analytics directly from ai_usage_events (when Edge Function unavailable). */
  async getUsageAnalyticsFromDb(
    userId: string,
    monthStr: string
  ): Promise<UsageAnalyticsResponse> {
    try {
      const [year, monthNum] = monthStr.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, monthNum - 1, 1));
      const endDate = new Date(Date.UTC(year, monthNum, 0, 23, 59, 59, 999));
      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();

      const { data: events, error } = await (supabase as any)
        .from('ai_usage_events')
        .select('id, event_type, request_count, cost_cents, metadata, created_at')
        .eq('user_id', userId)
        .gte('created_at', startIso)
        .lte('created_at', endIso)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: error.message || 'Failed to fetch usage analytics',
          },
        };
      }

      const rows = (events ?? []) as Array<{
        id: string;
        event_type: string;
        request_count: number;
        cost_cents: number;
        metadata: Record<string, unknown> | null;
        created_at: string;
      }>;

      const thisMonthCostCents = rows.reduce(
        (sum, r) => sum + Number(r.cost_cents ?? 0),
        0
      );
      const apiRequests = rows.reduce(
        (sum, r) => sum + (r.request_count ?? 1),
        0
      );

      let totalTransactions = 0;
      let totalAccepted = 0;
      let totalCorrect = 0;
      for (const r of rows) {
        const m = r.metadata ?? {};
        totalTransactions += Number(m.transaction_count ?? 0);
        totalAccepted += Number(m.accepted_count ?? 0);
        totalCorrect += Number(m.correct_count ?? 0);
      }

      const acceptedRatePercent =
        totalTransactions > 0 ? (totalAccepted / totalTransactions) * 100 : 0;
      const correctRatePercent =
        totalTransactions > 0 ? (totalCorrect / totalTransactions) * 100 : 0;
      const timeSavedHours = totalAccepted / 60;

      const eventTypeToDescription: Record<string, string> = {
        auto_categorise: 'Smart Categorisation',
        smart_insights: 'Smart Insights',
        budget_analysis: 'Budget Analysis',
        unusual_expense: 'Unusual Expense Detection',
        test_connection: 'Connection Test',
        receipt_scan: 'Receipt Scan',
        insights_chat: 'Insights Chat',
      };

      const recentActivity = rows.slice(0, 10).map((r) => ({
        id: r.id,
        eventType: r.event_type,
        description:
          eventTypeToDescription[r.event_type] ?? r.event_type.replace(/_/g, ' '),
        timestamp: r.created_at,
      }));

      const performanceSummary =
        rows.length === 0
          ? 'No AI activity yet'
          : `Accepted ${acceptedRatePercent.toFixed(1)}% of suggestions, ${correctRatePercent.toFixed(1)}% correct.`;

      return {
        data: {
          thisMonthCostCents: Math.round(thisMonthCostCents),
          apiRequests,
          acceptedRatePercent: Math.round(acceptedRatePercent * 10) / 10,
          correctRatePercent: Math.round(correctRatePercent * 10) / 10,
          timeSavedHours: Math.round(timeSavedHours * 100) / 100,
          recentActivity,
          performanceSummary,
        },
      };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to get usage analytics';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /**
   * T011: Check if user has exceeded monthly spend limit.
   * Supports one-time override per session (in-memory).
   */
  async checkSpendLimitAndOverride(
    userId?: string
  ): Promise<SpendLimitCheckResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      const now = new Date();
      const monthStr = now.toISOString().slice(0, 7);
      const [year, monthNum] = monthStr.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, monthNum - 1, 1));
      const endDate = new Date(Date.UTC(year, monthNum, 0, 23, 59, 59, 999));

      const { data: settingsRow } = await (supabase as any)
        .from('user_ai_settings')
        .select('monthly_spend_limit_cents')
        .eq('user_id', currentUserId)
        .single();

      const monthlyLimitCents = settingsRow?.monthly_spend_limit_cents ?? null;

      const { data: events } = await (supabase as any)
        .from('ai_usage_events')
        .select('cost_cents')
        .eq('user_id', currentUserId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const currentMonthSpendCents = (events ?? []).reduce(
        (sum: number, r: { cost_cents?: number }) =>
          sum + Number(r.cost_cents ?? 0),
        0
      );

      const limitReached =
        monthlyLimitCents != null && currentMonthSpendCents >= monthlyLimitCents;
      const overrideUsed = overrideUsedThisSession.has(currentUserId);
      const canOverride = limitReached && !overrideUsed;
      const allowed = !limitReached;

      return {
        data: {
          allowed,
          limitReached,
          currentMonthSpendCents: Math.round(currentMonthSpendCents),
          monthlyLimitCents,
          canOverride,
        },
      };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to check spend limit';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /**
   * T011: Record that user used one-time override this session.
   */
  async recordOverrideUsed(userId?: string): Promise<SuccessResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      overrideUsedThisSession.add(currentUserId);
      return { data: { success: true } };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to record override';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },

  /**
   * Get model mapping for each quality level for a provider.
   * Used to display model names in the Quality Level dropdown.
   */
  async getProviderModelMappings(
    provider: string
  ): Promise<Record<string, string>> {
    if (!provider || provider === 'none') return {};
    const { data: rows, error } = await (supabase as any)
      .from('ai_provider_model_mapping')
      .select('quality_level, model_id')
      .eq('provider', provider);

    if (error || !rows?.length) return {};

    return Object.fromEntries(
      rows.map((r: { quality_level: string; model_id: string }) => [
        r.quality_level,
        r.model_id,
      ])
    );
  },

  /**
   * T011/T023: Lookup provider model mapping from ai_provider_model_mapping.
   * Used when invoking AI and recording usage (cost calculation).
   * Returns fallbackModelId when primary is claude-3-5-opus (may not be available at launch).
   */
  async getModelForProvider(
    provider: string,
    qualityLevel: string
  ): Promise<{
    modelId: string;
    fallbackModelId?: string;
    costPerMillionInputCents: number;
    costPerMillionOutputCents: number;
  } | null> {
    const { data: row, error } = await (supabase as any)
      .from('ai_provider_model_mapping')
      .select('model_id, cost_per_million_input_cents, cost_per_million_output_cents')
      .eq('provider', provider)
      .eq('quality_level', qualityLevel)
      .single();

    if (error || !row) return null;

    const modelId = row.model_id;
    const fallbackModelId =
      modelId === 'claude-3-5-opus' ? 'claude-3-opus' : undefined;

    return {
      modelId,
      ...(fallbackModelId && { fallbackModelId }),
      costPerMillionInputCents: row.cost_per_million_input_cents ?? 0,
      costPerMillionOutputCents: row.cost_per_million_output_cents ?? 0,
    };
  },

  /**
   * Persist insight time-range settings used by Refresh Analysis.
   * Bypasses full settings validation so calendar-year custom dates are always saved.
   */
  async saveInsightTimeRangeSettings(
    userId: string | undefined,
    selectedValue: number
  ): Promise<SuccessResponse> {
    try {
      const currentUserId = userId ?? (await getCurrentUserId());
      if (!currentUserId) {
        return { error: { code: 'AUTH_ERROR', message: 'User is not authenticated' } };
      }

      const payload = buildInsightTimeRangeSavePayload(selectedValue);

      const { data: existingRow, error: existingError } = await (supabase as any)
        .from('user_ai_settings')
        .select('user_id, provider')
        .eq('user_id', currentUserId)
        .maybeSingle();

      if (existingError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: existingError.message || 'Failed to load settings',
          },
        };
      }

      const timeRangeUpdate = {
        insight_time_range_days: payload.insightTimeRangeDays,
        insight_custom_start_date: payload.insightCustomStartDate,
        insight_custom_end_date: payload.insightCustomEndDate,
        updated_at: new Date().toISOString(),
      };

      if (existingRow) {
        const { error } = await (supabase as any)
          .from('user_ai_settings')
          .update(timeRangeUpdate)
          .eq('user_id', currentUserId);

        if (error) {
          return { error: { code: 'UNKNOWN_ERROR', message: error.message || 'Failed to save time range' } };
        }
      } else {
        const { error } = await (supabase as any)
          .from('user_ai_settings')
          .insert({
            user_id: currentUserId,
            provider: DEFAULT_AI_SETTINGS.provider,
            quality_level: DEFAULT_AI_SETTINGS.qualityLevel,
            ...timeRangeUpdate,
          });

        if (error) {
          return { error: { code: 'UNKNOWN_ERROR', message: error.message || 'Failed to save time range' } };
        }
      }

      return { data: { success: true } };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save insight time range';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  },
};
